import { NgTemplateOutlet } from '@angular/common';
import type { OnChanges } from '@angular/core';
import { ChangeDetectionStrategy, Component, Input, output } from '@angular/core';

import { NgTemplateAssertType } from '@core/common-legacy';
import { I18nService } from '@core/i18n-legacy';
import { Currency } from '@core/pricing-types';
import type { BillingPlan } from '@core/products-types';
import type { PaymentInterval } from '@core/subscription-types';
import {
  DropdownItemComponent,
  DropdownComponent,
  ButtonDropdownIndicatorDirective,
  ButtonComponent,
  OverlayOriginDirective,
  ConnectedOverlayDirective,
} from '@core/user-interface-legacy';

@Component({
  selector: 'rt-plan-picker-global-controls',
  templateUrl: './plan-picker-global-controls.component.html',
  styleUrls: ['./plan-picker-global-controls.component.less'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    NgTemplateOutlet,
    NgTemplateAssertType,
    ButtonComponent,
    ButtonDropdownIndicatorDirective,
    OverlayOriginDirective,
    ConnectedOverlayDirective,
    DropdownComponent,
    DropdownItemComponent,
  ],
})
export class PlanPickerGlobalControlsComponent implements OnChanges {
  @Input() public enableCurrencyChange: boolean;
  @Input() public enableIntervalChange: boolean;
  @Input() public interval: PaymentInterval;
  @Input() public assignedProduct?: BillingPlan;
  @Input() public currency: Currency;
  @Input() public isCustomSubscription: boolean;

  public currencyChange = output<Currency>();
  public intervalChange = output<PaymentInterval>();

  public controlContextType: {
    options: PlanPickerGlobalControlsComponent['currencies'];
    value: Currency;
    kind: 'currency';
  };
  public label: {
    currency?: string;
    interval?: string;
  } = {};

  public currencies = [
    { label: 'EUR', value: Currency.EUR },
    { label: 'GBP', value: Currency.GBP },
    { label: 'USD', value: Currency.USD },
  ];

  public intervals: Array<{ label: string; value: number | string }> = [];

  constructor(private i18n: I18nService) {}

  public trackByOption(index: number) {
    return index;
  }

  public emitChange(kind: 'interval', value: PaymentInterval): void;
  public emitChange(kind: 'currency', value: Currency): void;
  public emitChange(kind: string, value: any) {
    // eslint-disable-next-line default-case
    switch (kind) {
      case 'currency':
        this.currencyChange.emit(value);
        break;

      case 'interval':
        this.intervalChange.emit(value);
        break;
    }
  }

  public ngOnInit() {
    this.setIntervals();
    this.setLabel();
  }

  public ngOnChanges() {
    this.setIntervals();
    this.setLabel();
  }

  private setLabel() {
    this.label.currency = this.currencies.find(c => c.value === this.currency)?.label;
    this.label.interval = this.intervals.find(c => c.value === this.interval)?.label;
  }

  private getIntervalLabel() {
    const remainder = +this.interval % 12;
    const numberOfYears = +this.interval / 12;

    if (numberOfYears === 1) {
      return this.i18n.translate('app.pricing.plan_picker.pay_annually_save');
    }

    if (numberOfYears > 1 && remainder === 0) {
      return this.i18n.translate('app.pricing.plan_picker.pay_every_x_years', {
        interval: numberOfYears,
      });
    }

    if (this.interval === 1) {
      return this.i18n.translate('app.pricing.plan_picker.pay_monthly');
    }

    return this.i18n.translate('app.pricing.plan_picker.pay_every_x_months', {
      interval: this.interval,
    });
  }

  private setIntervals() {
    this.interval = (<BillingPlan>this.assignedProduct)?.billingPeriod || this.interval;
    const standardIntervals = [
      {
        label: this.i18n.translate('app.pricing.plan_picker.pay_annually_save'),
        value: 12,
      },
      {
        label: this.i18n.translate('app.pricing.plan_picker.pay_monthly'),
        value: 1,
      },
    ];
    const customIntervals = [
      {
        label: this.getIntervalLabel(),
        value: this.interval,
      },
    ];
    this.intervals = this.isCustomSubscription ? customIntervals : standardIntervals;
  }
}
